import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  Autocomplete,
  Alert,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import api from "../../api/axios";

function normalizeTags(input) {
  const arr = Array.isArray(input) ? input : [];
  return Array.from(new Set(arr.map((t) => String(t || "").trim()).filter(Boolean)));
}

export default function PackScopeEditor({
  open,
  onClose,
  pack,
  onSaved,
  tagSuggestions = ["CCNA", "CCNP", "CCIE", "SECURITY", "WIRELESS"],
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const packId = pack?.id;

  const [loadingLabs, setLoadingLabs] = useState(false);
  const [saving, setSaving] = useState(false);

  const [labs, setLabs] = useState([]);
  const [labsTotal, setLabsTotal] = useState(0);
  const [labSearch, setLabSearch] = useState("");
  const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });

  // selection
  const initialScope = useMemo(() => pack?.lab_scope || {}, [pack]);
  const [selectedLabIds, setSelectedLabIds] = useState(() => new Set(initialScope.lab_ids || []));
  const [selectedTags, setSelectedTags] = useState(() => normalizeTags(initialScope.tags));

  useEffect(() => {
    // reset when opening / pack changes
    if (!open) return;
    const scope = pack?.lab_scope || {};
    setSelectedLabIds(new Set(Array.isArray(scope.lab_ids) ? scope.lab_ids : []));
    setSelectedTags(normalizeTags(scope.tags));
  }, [open, pack]);

  const labsQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(pageModel.page + 1));
    p.set("page_size", String(pageModel.pageSize));
    if (labSearch.trim()) p.set("search", labSearch.trim());
    return p.toString();
  }, [labSearch, pageModel]);

  const fetchLabs = useCallback(async () => {
    setLoadingLabs(true);
    try {
      // adapte si ton endpoint admin labs diffère
      const res = await api.get(`/admin/labs?${labsQuery}`);


      const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
    const total = Array.isArray(res.data) ? items.length : (res.data?.total ?? items.length);

      setLabs(items);
      setLabsTotal(total);
/*
      setLabs(res.data?.items || []);
      setLabsTotal(res.data?.total || 0); */
    } catch (e) {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setLoadingLabs(false);
    }
  }, [enqueueSnackbar, labsQuery, t]);

  useEffect(() => {
    if (!open) return;
    fetchLabs();
  }, [open, fetchLabs]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "name", headerName: t("packages.scope.labs.name"), flex: 1, minWidth: 220 },
      {
        field: "is_active",
        headerName: t("admin.packages.scope.labs.active"),
        width: 120,
        renderCell: (p) =>
          p.value ? <Chip size="small" label={t("admin.packages.active")} /> : <Chip size="small" variant="outlined" label={t("admin.packages.inactive")} />,
      },
    ],
    [t]
  );

  const selectionModel = useMemo(() => Array.from(selectedLabIds), [selectedLabIds]);

  const onSelectionChange = (newSelection) => {
    // newSelection = array of ids
    setSelectedLabIds(new Set(newSelection));
  };

  const save = useCallback(async () => {
    if (!packId) return;
    setSaving(true);
    try {
      const payload = {
        lab_ids: selectionModel.length ? selectionModel : null,
        tags: selectedTags.length ? selectedTags : null,
      };
      await api.post(`/admin/packages/packs/${packId}/scope`, payload);

      enqueueSnackbar(t("admin.packages.snack.scopeSaved", { code: pack?.code }), { variant: "success" });

      if (onSaved) onSaved(payload);
      onClose();
    } catch (e) {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  }, [enqueueSnackbar, onClose, onSaved, pack?.code, packId, selectionModel, selectedTags, t]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {t("admin.packages.scope.editorTitle", { code: pack?.code || "" })}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            <Typography variant="body2">
              {t("admin.packages.scope.editorHelp")}
            </Typography>
          </Alert>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t("admin.packages.scope.tagsTitle")}
            </Typography>

            <Autocomplete
              multiple
              freeSolo
              options={tagSuggestions}
              value={selectedTags}
              onChange={(e, v) => setSelectedTags(normalizeTags(v))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => <Chip variant="outlined" label={option} {...getTagProps({ index })} />)
              }
              renderInput={(params) => (
                <TextField {...params} label={t("admin.packages.scope.tags")} placeholder={t("admin.packages.scope.tagsPlaceholder")} />
              )}
              disabled={saving}
            />
          </Box>

          <Divider />

          <Box>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {t("admin.packages.scope.labsTitle")}
              </Typography>

              <TextField
                size="small"
                value={labSearch}
                onChange={(e) => setLabSearch(e.target.value)}
                label={t("common.search")}
                sx={{ width: { xs: "100%", md: 320 } }}
                disabled={saving}
              />

              <Button
                variant="outlined"
                startIcon={loadingLabs ? <CircularProgress size={18} /> : <RefreshIcon />}
                onClick={fetchLabs}
                disabled={loadingLabs || saving}
              >
                {t("common.refresh")}
              </Button>
            </Stack>

            <Box sx={{ height: 520 }}>
              <DataGrid
                rows={labs}
                columns={columns}
                getRowId={(r) => r.id}
                loading={loadingLabs}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={onSelectionChange}
                paginationMode="server"
                rowCount={labsTotal}
                paginationModel={pageModel}
                onPaginationModelChange={setPageModel}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("admin.packages.scope.selectedCount", { n: selectionModel.length })}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={save}
          disabled={saving || !packId}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
